import React, { useState, useCallback, useMemo } from 'react';
import './LogFilterBar.css';

const EMPTY_FILTERS = {
  search: '',
  agent: '',
  source: '',
  status: '',
  dateFrom: '',
  dateTo: '',
  costMin: '',
  costMax: '',
};

/**
 * Shared filter state for the log views. Keys not used by a given view simply
 * stay empty and are ignored by that view's predicate.
 */
export const useLogFilters = (initial = {}) => {
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, ...initial });

  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({ ...EMPTY_FILTERS, ...initial });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeCount = useMemo(
    () => Object.values(filters).filter(v => v !== '' && v != null).length,
    [filters]
  );

  return { filters, setFilter, resetFilters, activeCount };
};

/**
 * Inclusive date-range test against a timestamp.
 * `from` / `to` are `yyyy-mm-dd` strings from <input type="date">; `to` covers
 * the whole selected day rather than midnight on it.
 */
export const matchesDateRange = (timestamp, from, to) => {
  if (!from && !to) return true;
  if (!timestamp) return false;

  const t = new Date(timestamp).getTime();
  if (Number.isNaN(t)) return false;

  if (from) {
    const start = new Date(`${from}T00:00:00`).getTime();
    if (!Number.isNaN(start) && t < start) return false;
  }
  if (to) {
    const end = new Date(`${to}T23:59:59.999`).getTime();
    if (!Number.isNaN(end) && t > end) return false;
  }
  return true;
};

const IcSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IcFilter = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

/**
 * Search + filter controls shared by the Conversation and Workflow log views.
 *
 * On mobile the advanced filters collapse behind a "Filters" toggle so the
 * search box alone occupies the initial viewport; the toggle carries a count
 * badge so active filters are never hidden silently.
 */
const LogFilterBar = ({
  filters,
  setFilter,
  onReset,
  activeCount = 0,
  selects = [],
  showCost = false,
  showDate = true,
  searchPlaceholder = 'Search…',
  resultLabel,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="logf">
      <div className="logf-top">
        <div className="logf-search">
          <span className="logf-search-icon"><IcSearch /></span>
          <input
            type="search"
            className="logf-search-input"
            value={filters.search}
            onChange={e => setFilter('search', e.target.value)}
            placeholder={searchPlaceholder}
            aria-label="Search logs"
          />
          {filters.search && (
            <button
              type="button"
              className="logf-search-clear"
              onClick={() => setFilter('search', '')}
              aria-label="Clear search"
            >×</button>
          )}
        </div>

        <button
          type="button"
          className={`logf-toggle${expanded ? ' logf-toggle--on' : ''}`}
          onClick={() => setExpanded(v => !v)}
          aria-expanded={expanded}
        >
          <IcFilter />
          <span>Filters</span>
          {activeCount > 0 && <span className="logf-count">{activeCount}</span>}
        </button>
      </div>

      <div className={`logf-panel${expanded ? ' logf-panel--open' : ''}`}>
        {selects.map(({ key, label, options, allLabel }) => (
          <label className="logf-field" key={key}>
            <span className="logf-label">{label}</span>
            <select
              className="logf-control"
              value={filters[key] ?? ''}
              onChange={e => setFilter(key, e.target.value)}
            >
              <option value="">{allLabel || `All ${label.toLowerCase()}`}</option>
              {options.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        ))}

        {showDate && (
          <>
            <label className="logf-field">
              <span className="logf-label">From</span>
              <input
                type="date"
                className="logf-control"
                value={filters.dateFrom}
                max={filters.dateTo || undefined}
                onChange={e => setFilter('dateFrom', e.target.value)}
              />
            </label>
            <label className="logf-field">
              <span className="logf-label">To</span>
              <input
                type="date"
                className="logf-control"
                value={filters.dateTo}
                min={filters.dateFrom || undefined}
                onChange={e => setFilter('dateTo', e.target.value)}
              />
            </label>
          </>
        )}

        {showCost && (
          <>
            <label className="logf-field logf-field--narrow">
              <span className="logf-label">Min cost $</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                className="logf-control"
                value={filters.costMin}
                onChange={e => setFilter('costMin', e.target.value)}
                placeholder="0.00"
              />
            </label>
            <label className="logf-field logf-field--narrow">
              <span className="logf-label">Max cost $</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                className="logf-control"
                value={filters.costMax}
                onChange={e => setFilter('costMax', e.target.value)}
                placeholder="Any"
              />
            </label>
          </>
        )}

        <div className="logf-actions">
          <button
            type="button"
            className="logf-reset"
            onClick={onReset}
            disabled={activeCount === 0}
          >
            Clear all
          </button>
        </div>
      </div>

      {resultLabel && <div className="logf-result">{resultLabel}</div>}
    </div>
  );
};

export default LogFilterBar;
